import React from "react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMap } from "../hooks";
import { FullScreen } from "ol/control";
import { ControlGroup } from "./layout/ControlGroup";
import { Button } from "./element/Button";
import { useEffectIfMounted } from "../hooks/useEffectIfMounted";
import { FullScreenIcon } from "../constants/icons/FullScreenIcon";
import { FullScreenExitIcon } from "../constants/icons/FullScreenExitIcon";
import { Icon } from "src/domains/shared/components/Icon";
import {
  FULLSCREEN_TOGGLE,
  MAP_TOOL_BUTTON,
  MAP_TOOL_ICON_BOX,
} from "src/domains/shared/constants/mapToolButton";

/**
 * 전체화면 토글 컨트롤 props — 내부 토글 상태 자체 관리, 전환 시 `onChange(isFull)` 호출.
 */
interface FullScreenFeatureProps {
  onChange?: (isFull: boolean) => void;
}

export const FullScreenFeature = ({ onChange }: FullScreenFeatureProps) => {
  const { t } = useTranslation("timeSeries");
  const map = useMap();

  const ref = useRef<HTMLButtonElement>(null);
  const onBtnRef = useRef<HTMLButtonElement | null>(null);
  const offBtnRef = useRef<HTMLButtonElement | null>(null);
  const [isFull, setIsFull] = useState(false);

  useEffectIfMounted(() => {
    if (!map) return;
    const targetMapId = map.getTargetElement().getAttribute("id") as string;

    onBtnRef.current = document.querySelector<HTMLButtonElement>(
      `#${CSS.escape(targetMapId)} .ol-full-screen-false`
    );

    offBtnRef.current = document.querySelector<HTMLButtonElement>(
      `#${CSS.escape(targetMapId)} .ol-full-screen-true`
    );
  }, [isFull, map]);

  const toggleFullScreen = () => {
    if (isFull) {
      offBtnRef.current?.click();
    } else {
      onBtnRef.current?.click();
    }
  };

  const onFull = () => {
    setIsFull(true);
    onChange && onChange(true);
  };

  const offFull = () => {
    setIsFull(false);
    onChange && onChange(false);
  };

  useEffect(() => {
    if (!map || !ref.current) return;

    const fullScreenSource = new FullScreen({
      target: ref.current,
    });

    fullScreenSource.on("enterfullscreen", onFull);
    fullScreenSource.on("leavefullscreen", offFull);

    map.addControl(fullScreenSource);

    return () => {
      map.removeControl(fullScreenSource);
      fullScreenSource.un("enterfullscreen", onFull);
      fullScreenSource.un("leavefullscreen", offFull);
    };
  }, [map]);

  return (
    <ControlGroup>
      <Button
        ref={ref}
        onClick={toggleFullScreen}
        aria-label={t(
          isFull
            ? FULLSCREEN_TOGGLE.labelKey.exit
            : FULLSCREEN_TOGGLE.labelKey.enter,
          { ns: "timeSeries" }
        )}
        aria-pressed={isFull}
      >
        <Icon
          size={24}
          clickable
          sx={MAP_TOOL_ICON_BOX}
          color={MAP_TOOL_BUTTON.idleIcon}
          name={
            isFull ? FULLSCREEN_TOGGLE.icon.exit : FULLSCREEN_TOGGLE.icon.enter
          }
        />
      </Button>
    </ControlGroup>
  );
};
